import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Phone } from "lucide-react";
import { TopographicBackground, PaperGrain, GoldOrb, StarField } from "./TopographicBackground";
import { ArtDivider } from "./ArtDivider";

const EASE = [0.22, 1, 0.36, 1];

function Block({ block, i, isLead }) {
  const anim = {
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay: 0.1 + i * 0.05, ease: EASE },
  };

  if (block.type === "h") {
    return (
      <>
        <ArtDivider className="my-4" />
        <motion.h3
          {...anim}
          className="headline text-[26px] font-medium text-ink sm:text-[32px]"
        >
          {block.text}
        </motion.h3>
      </>
    );
  }
  if (block.type === "quote") {
    return (
      <motion.blockquote
        {...anim}
        className="my-12 border-l-2 border-gold/60 pl-7"
      >
        <p className="headline text-3xl italic text-gold-deep sm:text-4xl">{block.text}</p>
      </motion.blockquote>
    );
  }
  if (block.type === "question") {
    return (
      <motion.div {...anim} className="mt-14">
        <div className="gold-rule mb-6 max-w-[120px]" />
        <p className="deck text-xl not-italic sm:text-2xl" style={{ fontStyle: "italic", color: "#2C2E33" }}>
          {block.text}
        </p>
      </motion.div>
    );
  }
  if (block.type === "ul") {
    return (
      <motion.ul {...anim} className="mt-7 space-y-5">
        {block.items.map((it, k) => (
          <li key={k} className="prose-editorial flex gap-4">
            <span className="mt-3 h-1.5 w-1.5 flex-none rounded-full bg-gold" aria-hidden="true" />
            <span className="text-[18px] leading-relaxed">
              <span className="font-display text-[19px] font-medium text-ink">{it.lead}.</span>{" "}
              {it.text}
            </span>
          </li>
        ))}
      </motion.ul>
    );
  }
  return (
    <motion.p
      {...anim}
      className={`prose-editorial ${isLead ? "dropcap text-[20px] leading-[1.8]" : "text-[19px]"} mt-7`}
    >
      {block.text}
    </motion.p>
  );
}

export const Preface = ({ open, onClose, book, onEnterBook }) => {
  const preface = book?.preface;
  const callUrl = book?.links?.braced_call;
  let leadUsed = false;

  return (
    <AnimatePresence>
      {open && preface && (
        <motion.div
          className="fixed inset-0 z-[90] overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          data-testid="preface-overlay"
        >
          <div className="fixed inset-0 watercolor-wash" />
          <PaperGrain />
          <GoldOrb className="left-1/2 top-[-120px] -translate-x-1/2" size={520} testId="preface-orb" />
          <StarField count={18} className="opacity-70" />
          <TopographicBackground
            className="pointer-events-none absolute inset-x-0 top-0 h-[460px] w-full"
            opacity={0.5}
            animate
          />

          <div className="relative z-10 mx-auto max-w-3xl px-6 py-10 sm:px-10 sm:py-16">
            <div className="mb-12 flex items-start justify-between sm:mb-16">
              <div>
                <span className="kicker text-gold-foil">{preface.eyebrow || "Before You Begin"}</span>
                <span className="kicker mt-2 block text-ink-muted">
                  From <span className="italic normal-case tracking-normal">Braced No More</span> · Casey Muze
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close preface"
                data-testid="close-preface-btn"
                className="debossed-btn flex h-10 w-10 flex-none items-center justify-center rounded-full text-ink-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
              >
                <X strokeWidth={1.4} className="h-5 w-5" />
              </button>
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="headline text-[44px] leading-[0.98] text-ink sm:text-[68px]"
            >
              {preface.title}
            </motion.h2>
            <div className="gold-rule my-8 max-w-[180px]" />

            <div className="measure">
              {preface.blocks.map((b, i) => {
                const isLead = b.type === "p" && !leadUsed;
                if (isLead) leadUsed = true;
                return <Block key={i} block={b} i={i} isLead={isLead} />;
              })}
            </div>

            <div className="mt-16 border-t border-gold/20 pt-10">
              <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onEnterBook?.();
                  }}
                  data-testid="preface-begin-btn"
                  className="debossed-btn rounded-full px-8 py-3.5 font-display text-lg text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                >
                  Begin reading
                </button>
                {callUrl && (
                  <a
                    href={callUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="preface-braced-call"
                    className="inline-flex items-center gap-2 text-base text-gold-deep underline-offset-4 transition hover:gap-3 hover:underline"
                  >
                    <Phone strokeWidth={1.4} className="h-4 w-4" />
                    Book a free Braced Call
                  </a>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
